import { useMemo } from "react";
import { categories, platforms } from "@/mocks/platforms";

interface RankBoardProps {
  query: string;
  onQueryChange: (value: string) => void;
  activeCategory: string;
  onCategoryChange: (value: string) => void;
}

export default function RankBoard({ query, onQueryChange, activeCategory, onCategoryChange }: RankBoardProps) {
  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return platforms
      .filter((item) => (activeCategory === "all" ? true : item.category === activeCategory))
      .filter((item) => {
        if (!keyword) return true;
        return (
          item.name.toLowerCase().includes(keyword) ||
          item.tagline.toLowerCase().includes(keyword) ||
          item.categoryName.toLowerCase().includes(keyword) ||
          item.tags.some((tag) => tag.toLowerCase().includes(keyword))
        );
      })
      .sort((a, b) => b.heat - a.heat);
  }, [query, activeCategory]);

  return (
    <section id="rank" className="bg-background-100 py-16 md:py-24 border-y border-background-200">
      <div className="mx-auto max-w-shell px-4 md:px-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <span className="text-xs font-label tracking-[0.2em] text-primary-600 uppercase">Hot Ranking</span>
            <h2 className="mt-3 font-heading font-bold text-foreground-950 text-2xl md:text-4xl">爆料网站人气热度榜</h2>
            <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
              按更新频率、话题广度、讨论活跃度与用户反馈加权得出的综合热度排名。切换分类或输入关键词，就能立刻筛出你想看的阵地。
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-foreground-500 whitespace-nowrap">
            <span className="w-8 h-8 flex items-center justify-center rounded-md bg-background-50 border border-background-300 text-foreground-700">
              <i className="ri-sort-desc"></i>
            </span>
            共 {filtered.length} 个平台在榜
          </div>
        </div>

        <div className="mt-8 flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => {
              const active = cat.id === activeCategory;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => onCategoryChange(cat.id)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    active
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-50 text-foreground-700 border border-background-300 hover:border-primary-300 hover:text-primary-600"
                  }`}
                >
                  <i className={cat.icon}></i>
                  {cat.name}
                </button>
              );
            })}
          </div>

          <div className="lg:ml-auto flex items-center gap-2 w-full lg:w-[280px] px-3 py-2 rounded-md bg-background-50 border border-background-300">
            <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line"></i>
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="搜索榜单平台"
              aria-label="搜索榜单平台"
              className="w-full text-sm bg-transparent text-foreground-900 placeholder:text-foreground-400 outline-none"
            />
            {query && (
              <button
                type="button"
                aria-label="清空搜索"
                onClick={() => onQueryChange("")}
                className="w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-circle-line"></i>
              </button>
            )}
          </div>
        </div>

        <div className="mt-8 space-y-3">
          {filtered.length === 0 && (
            <div className="py-16 text-center rounded-lg bg-background-50 border border-dashed border-background-300">
              <span className="mx-auto w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                <i className="ri-emotion-sad-line text-2xl"></i>
              </span>
              <p className="mt-4 text-sm text-foreground-600">没有匹配的平台，换个关键词或切回「全部」试试。</p>
              <button
                type="button"
                onClick={() => {
                  onQueryChange("");
                  onCategoryChange("all");
                }}
                className="mt-4 px-4 py-2 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                重置筛选
              </button>
            </div>
          )}

          {filtered.map((item, index) => (
            <article
              key={item.id}
              className="animate-rise group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-start bg-background-50 rounded-lg border border-background-200 p-4 md:p-5 hover:border-primary-300 transition-colors"
              style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
            >
              <div className="md:col-span-1 flex md:flex-col items-center md:items-start gap-3 md:gap-1">
                <span
                  className={`font-heading font-bold text-2xl md:text-3xl leading-none ${
                    index < 3 ? "text-primary-600" : "text-foreground-300"
                  }`}
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
                {index < 3 && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-accent-700 bg-accent-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                    <i className="ri-fire-fill"></i>TOP
                  </span>
                )}
              </div>

              <div className="md:col-span-7">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-heading font-bold text-lg text-foreground-950">{item.name}</h3>
                  <span className="px-2 py-0.5 text-[11px] rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap">
                    {item.categoryName}
                  </span>
                  {item.badge && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                      <i className="ri-award-line"></i>
                      {item.badge}
                    </span>
                  )}
                </div>
                <p className="mt-1.5 text-sm text-primary-700">{item.tagline}</p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.description}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {item.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 text-[11px] rounded-md bg-background-100 text-foreground-600 border border-background-200">
                      {tag}
                    </span>
                  ))}
                  <span className="px-2.5 py-1 text-[11px] rounded-md bg-background-100 text-foreground-500 border border-background-200">
                    创站 {item.established}
                  </span>
                </div>
              </div>

              <div className="md:col-span-4 w-full">
                <div className="flex items-center justify-between text-xs text-foreground-500">
                  <span>综合热度</span>
                  <span className="font-heading text-base font-bold text-foreground-950">{item.heat}</span>
                </div>
                <div className="mt-2 h-1.5 w-full rounded-full bg-background-200 overflow-hidden">
                  <div className="h-full rounded-full bg-primary-500" style={{ width: `${item.heat}%` }}></div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md bg-background-100 py-2">
                    <p className="inline-flex items-center gap-1 text-sm font-semibold text-foreground-900">
                      <i className="ri-star-fill text-accent-500"></i>
                      {item.rating}
                    </p>
                    <p className="mt-0.5 text-[11px] text-foreground-500">{item.reviews} 评分</p>
                  </div>
                  <div className="rounded-md bg-background-100 py-2">
                    <p className="text-sm font-semibold text-foreground-900">{item.updateScore}</p>
                    <p className="mt-0.5 text-[11px] text-foreground-500">更新分</p>
                  </div>
                  <div className="rounded-md bg-background-100 py-2">
                    <p className="text-sm font-semibold text-foreground-900">{item.trustScore}</p>
                    <p className="mt-0.5 text-[11px] text-foreground-500">可靠分</p>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <p className="mt-6 text-xs text-foreground-500">
          说明：热度与评分来自站内统计与用户反馈汇总，仅供横向比较参考，不构成对平台内容的背书。
        </p>
      </div>
    </section>
  );
}